/** F7 · Assessment (Studio tab): AI-drafted levels labelled Draft, approve or adjust each, edit the feedback, release (screens §F7; ADR-0026 §8d). */
import { useEffect, useState } from "react";
import { ROUTES, type AssessmentDetail, type AssessmentSummary, type BoardTeamRow } from "@bsim/contracts";
import type { FeedbackItem } from "@bsim/domain";
import { useRoute } from "../api/context";
import { GxLoading } from "../theme/GxLoading";
import { EmptyState } from "./EmptyState";
import { GxCard, type Tone } from "../theme/primitives";
import { GxPill } from "../theme/GxPill";
import { COMPETENCY_NAMES, EvidenceMatrixView } from "./EvidenceMatrixView";
import type { Act } from "./useAct";

type Competency = keyof typeof COMPETENCY_NAMES;
type Level = 1 | 2 | 3 | 4;
interface Decision { readonly decision: "approvedDraft" | "adjusted"; readonly level: Level; readonly note: string }

const LEVEL_WORDS: Readonly<Record<Level, string>> = { 1: "Not yet", 2: "Developing", 3: "Proficient", 4: "Advanced" };
const STATUS_TONE: Readonly<Record<string, Tone>> = { needs_review: "warn", released: "good", approved: "info" };
const LEVELS: readonly Level[] = [1, 2, 3, 4];

const initial = (detail: AssessmentDetail): Readonly<Partial<Record<Competency, Decision>>> =>
  Object.fromEntries(detail.competencies.flatMap((c) => (c.decision === null || c.facilitatorLevel === null ? [] : [[c.competency, { decision: c.decision, level: c.facilitatorLevel, note: "" } satisfies Decision]])));

const toReview = (s: AssessmentSummary): number => s.competencies.filter((c) => c.decision === null).length;

function Levels({ detail, decisions, onDecide }: { readonly detail: AssessmentDetail; readonly decisions: Readonly<Partial<Record<Competency, Decision>>>; readonly onDecide: (c: Competency, d: Decision) => void }) {
  return (
    <div className="gx-fac-levels">
      {detail.competencies.map((c) => {
        const d = decisions[c.competency];
        return (
          <div key={c.competency} className="gx-fac-level" data-review={c.needsReview} data-testid={`level.${c.competency}`}>
            <div>
              <b>{COMPETENCY_NAMES[c.competency]}</b>
              <div className="gx-small gx-muted">
                {c.aiDraftLevel === null ? "No draft level. Set one yourself from the evidence below, or leave it." : <>AI level {c.aiDraftLevel} · {LEVEL_WORDS[c.aiDraftLevel]} </>}
                {c.aiDraftLevel === null ? null : <GxPill tone="neutral">Draft</GxPill>}{c.needsReview && d === undefined ? <> <GxPill tone="warn">Needs your review</GxPill></> : null}
              </div>
            </div>
            <div className="gx-fac-level-acts">
              {c.aiDraftLevel === null ? null : <button type="button" className="gx-btn gx-btn--secondary" aria-pressed={d?.decision === "approvedDraft"}
                onClick={() => { if (c.aiDraftLevel !== null) onDecide(c.competency, { decision: "approvedDraft", level: c.aiDraftLevel, note: "" }); }}>Approve draft</button>}
              <select className="gx-fac-input gx-select" aria-label={`Adjust ${COMPETENCY_NAMES[c.competency]} to`} value={d?.decision === "adjusted" ? d.level : ""}
                onChange={(e) => { if (e.target.value !== "") onDecide(c.competency, { decision: "adjusted", level: Number(e.target.value) as Level, note: d?.note ?? "" }); }}>
                <option value="">Adjust to…</option>
                {LEVELS.map((l) => <option key={l} value={l}>Adjust to {l} · {LEVEL_WORDS[l]}</option>)}
              </select>
              {d?.decision === "adjusted" ? <input className="gx-fac-input" aria-label={`Note for ${COMPETENCY_NAMES[c.competency]}`} placeholder="Why" value={d.note} onChange={(e) => onDecide(c.competency, { ...d, note: e.target.value })} /> : null}
              <span data-testid={`decision.${c.competency}`}><GxPill tone={d === undefined ? "warn" : "good"}>{d === undefined ? "Not decided" : d.decision === "approvedDraft" ? `Approved at ${d.level}` : `Adjusted to ${d.level}`}</GxPill></span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const FIELDS = [["citedBehavior", "What they did"], ["whyItMatters", "Why it matters"], ["tryNextTime", "Try next time"], ["reflect", "Reflect"]] as const;

function Feedback({ items, onChange }: { readonly items: readonly FeedbackItem[]; readonly onChange: (i: number, patch: Partial<FeedbackItem>) => void }) {
  return (
    <div className="gx-fac-feedback">
      {items.map((f, i) => (
        <fieldset key={`${f.kind}.${f.competency}.${i}`} className="gx-fac-fb">
          <legend><GxPill tone={f.kind === "strength" ? "good" : "warn"}>{f.kind === "strength" ? "Strength" : "Growth area"}</GxPill> {COMPETENCY_NAMES[f.competency]} · round {f.round}</legend>
          {FIELDS.map(([key, label]) => (
            <label key={key} className="gx-fac-field"><span>{label}</span><textarea className="gx-fac-input" rows={2} value={f[key]} onChange={(e) => onChange(i, { [key]: e.target.value })} /></label>
          ))}
        </fieldset>
      ))}
    </div>
  );
}

function Detail({ cohortId, summary, teamName, act }: { readonly cohortId: string; readonly summary: AssessmentSummary; readonly teamName: string; readonly act: Act }) {
  const detail = useRoute("assessmentGet", { cohortId, assessmentId: summary.id });
  const [decisions, setDecisions] = useState<Readonly<Partial<Record<Competency, Decision>>>>({});
  const [feedback, setFeedback] = useState<readonly FeedbackItem[]>([]);
  const [saved, setSaved] = useState(false);
  const d = detail.data;
  useEffect(() => {
    if (d === undefined) return;
    setDecisions(initial(d));
    setFeedback(d.feedback.length > 0 ? d.feedback : d.aiFeedbackDraft);
  }, [d]);
  if (d === undefined) return <GxCard title={teamName}><GxLoading words="Loading the assessment…" height={96} /></GxCard>;

  const decided = d.competencies.every((c) => decisions[c.competency] !== undefined);
  const save = (release: boolean) => act("assessmentReview", { cohortId, assessmentId: summary.id }, {
    levels: d.competencies.flatMap((c) => { const x = decisions[c.competency]; return x === undefined ? [] : [{ competency: c.competency, decision: x.decision, level: x.level, ...(x.note.trim() === "" ? {} : { note: x.note }) }]; }),
    feedback: [...feedback], release,
  });
  const submit = async (release: boolean) => { setSaved(false); const r = await save(release); if (r.ok && !release) setSaved(true); };

  return (
    <div data-testid="assessment.detail">
      <GxCard title={teamName} aside={<GxPill tone={STATUS_TONE[d.status] ?? "neutral"}>{d.status.replace("_", " ")}</GxPill>} className="gx-fac-panel">
        <div className="gx-card-b"><Levels detail={d} decisions={decisions} onDecide={(c, x) => setDecisions((prev) => ({ ...prev, [c]: x }))} /></div>
      </GxCard>
      <GxCard title="Evidence" className="gx-fac-panel" aside={<span className="gx-small gx-faint">Indicators by round</span>}><EvidenceMatrixView matrix={d.evidenceMatrix} /></GxCard>
      <GxCard title="Feedback" className="gx-fac-panel" aside={<span className="gx-small gx-faint">Participants see this, never levels</span>}>
        <div className="gx-card-b"><Feedback items={feedback} onChange={(i, patch) => setFeedback((prev) => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)))} /></div>
      </GxCard>
      <div className="gx-fac-releasebar">
        <span className="gx-small gx-muted">{decided ? "Every competency has a decision." : "Approve or adjust every competency to release."}</span>
        <button type="button" className="gx-btn gx-btn--secondary" disabled={Object.keys(decisions).length === 0} data-testid="assessment.save" onClick={() => void submit(false)}>Save review</button>
        {saved ? <span className="gx-small gx-muted" role="status" data-testid="assessment.saved">Saved</span> : null}
        <button type="button" className="gx-btn gx-btn--accent" disabled={!decided} data-testid="assessment.release" onClick={() => void submit(true)}>Release feedback</button>
      </div>
    </div>
  );
}

export function AssessmentReview({ cohortId, teams, act, programId }: { readonly cohortId: string; readonly teams: readonly BoardTeamRow[]; readonly act: Act; readonly programId: string | null }) {
  const list = useRoute("assessmentList", { cohortId });
  const [selected, setSelected] = useState<string | null>(null);
  const items = list.data?.items ?? [];
  const current = items.find((i) => i.id === selected) ?? items[0];
  const nameOf = (teamId: string) => teams.find((t) => t.teamId === teamId)?.name ?? "Team";
  return (
    <div className="gx-fac-assess" data-testid="studio.assessment">
      <aside className="gx-card gx-fac-teamlist" aria-label="Teams">
        {items.length === 0 ? <EmptyState icon="check" title="No assessments to review yet" /> : items.map((i) => (
          <button key={i.id} type="button" className="gx-fac-teamrow" aria-current={i.id === current?.id} onClick={() => setSelected(i.id)}>
            <b>{nameOf(i.teamId)}</b>
            <span className="gx-small gx-muted">{i.status.replace("_", " ")}{toReview(i) > 0 ? ` · ${toReview(i)} to review` : ""}</span>
          </button>
        ))}
        {programId === null ? null : <a className="gx-btn gx-btn--ghost" href={ROUTES.soloProgressCsv.path.replace(":programId", encodeURIComponent(programId))} download>Solo progress (CSV)</a>}
      </aside>
      {current === undefined ? null : <Detail key={current.id} cohortId={cohortId} summary={current} teamName={nameOf(current.teamId)} act={act} />}
    </div>
  );
}
